"use client";
import { useState, useMemo, useEffect } from "react";
import { parseLocalDate } from "../../utils/commandCenter";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { formatCurrency, formatNumber } from "../../utils/helper";
import YearDropdown from "./yeardropdown";
import IncludePendingToggle from "./include-pending-toggle";
import { ChevronDown, FileUp } from "lucide-react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  commissionForAgent,
  dealCreditsAgent,
  isClosedDeal,
  isPendingDeal,
} from "../../utils/dealCredit";

export default function AgentProductionTab() {
  const [{ users, deals }, dispatch] = useStateValue();
  const currentYear = new Date().getFullYear();

  const fromYearsBase = Array.from(
    { length: 5 },
    (_, i) => currentYear - (i + 1),
  );
  const toYears = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const [toYear, setToYear] = useState(toYears[0]);
  const [fromYear, setFromYear] = useState(fromYearsBase[0]);
  const [fromVisible, setFromVisible] = useState(false);
  const [toVisible, setToVisible] = useState(false);
  const [includePending, setIncludePending] = useState(false);

  const filteredFromYears = useMemo(() => {
    return fromYearsBase.filter((year) => year < toYear);
  }, [toYear]);

  const allYearsRange = useMemo(() => {
    const years = [];
    for (
      let y = fromYearsBase[fromYearsBase.length - 1];
      y <= toYears[0];
      y++
    ) {
      years.push(y);
    }
    return years;
  }, [fromYearsBase, toYears]);

  const agentCards = useMemo(() => {
    if (!users?.length) return [];

    return users
      .filter(
        (user) =>
          (user.role === "Admin" || user.role === "Employee") &&
          user.type !== 1, // ✅ exclude type 1 users
      )
      .map((user) => ({
        id: user.id,
        firstname: user.firstname,
        lastname: user.lastname,
        role: user.role,
        email: user.email,
        type: user.type ? user.type : 0,
      }));
  }, [users]);

  const agentYearlyData = useMemo(() => {
    if (!agentCards.length) return [];

    return agentCards.map((user) => {
      const yearlyStats = {};

      // ✅ ALL possible years (dropdown range) — closed and pending are
      // tracked separately so the "Include pending" switch can blend them.
      allYearsRange.forEach((year) => {
        yearlyStats[year] = {
          year,
          closedVolume: 0,
          dealsCount: 0,
          netCommission: 0,
          pendingVolume: 0,
          pendingDealsCount: 0,
          pendingCommission: 0,
        };
      });

      if (deals?.length) {
        deals.forEach((deal) => {
          // Two separate questions, deliberately answered separately.
          // ownsUnit: is this person THE one agent credited with the
          // transaction and its sale price? Exactly one person per deal, so
          // volume and deal counts can never be booked twice.
          // commissionShare: what did this person actually get paid on it?
          // Everyone paid sees their own slice, so a referring agent shows
          // dollars with zero units, and someone who is both the agent and
          // the team lead is paid both slices on the one deal he counts.
          const ownsUnit = dealCreditsAgent(deal, user.id);
          const commissionShare = commissionForAgent(deal, user.id);
          if (!ownsUnit && !commissionShare) return; // nothing to record

          const closed = isClosedDeal(deal);
          if (!closed && !isPendingDeal(deal)) return; // cancelled/expired never count
          if (!deal.closingdate) return;

          const dealYear = parseLocalDate(deal.closingdate).getFullYear();
          const bucket = yearlyStats[dealYear];
          if (!bucket) return;

          if (closed) {
            if (ownsUnit) {
              bucket.closedVolume += Number(deal.offerPrice) || 0;
              bucket.dealsCount += 1;
            }
            bucket.netCommission += commissionShare;
          } else {
            if (ownsUnit) {
              bucket.pendingVolume += Number(deal.offerPrice) || 0;
              bucket.pendingDealsCount += 1;
            }
            bucket.pendingCommission += commissionShare;
          }
        });
      }

      return {
        ...user,
        yearlyStats,
      };
    });
  }, [agentCards, deals, allYearsRange]);

  // What the cards and the Excel export actually show: closed only, or
  // closed + pending when the switch is on. Avg sale price follows suit.
  const displayStats = (stats) => {
    const s = stats || {};
    const volume =
      (s.closedVolume || 0) + (includePending ? s.pendingVolume || 0 : 0);
    const count =
      (s.dealsCount || 0) + (includePending ? s.pendingDealsCount || 0 : 0);
    return {
      closedVolume: volume,
      dealsCount: count,
      netCommission:
        (s.netCommission || 0) +
        (includePending ? s.pendingCommission || 0 : 0),
      avgSalePrice: count > 0 ? volume / count : 0,
    };
  };

  const metricLabels = {
    closedVolume: includePending ? "Volume (Closed + Pending)" : "Closed Volume",
    dealsCount: includePending
      ? "Transactions (Closed + Pending)"
      : "Transactions",
    netCommission: includePending
      ? "Commission (Closed + Pending)"
      : "Closed Commission",
    avgSalePrice: includePending
      ? "Avg Sale Price (Closed + Pending)"
      : "Avg Sale Price",
  };

  const getInitials = (name) => {
    if (!name) return "NA";

    const parts = name.trim().split(" ").filter(Boolean);

    if (parts.length === 1) {
      // single name → first 2 letters
      return parts[0].slice(0, 2).toUpperCase();
    }

    // otherwise → first two words ka first letter
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const exportToExcel = async (fromYear, toYear) => {
    if (!fromYear || !toYear) {
      toast.error("Please select From and To year first.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Agent Production");

    // ✅ Only selected years
    const years = [fromYear.toString(), toYear.toString()];

    const metrics = [
      "closedVolume",
      "dealsCount",
      "netCommission",
      "avgSalePrice",
    ];

    // 🔹 Header row
    const headerRowValues = ["Agent", "Role", "Email"];
    metrics.forEach((metric) => {
      years.forEach((year) => {
        headerRowValues.push(`${year} ${metricLabels[metric]}`);
      });
    });

    const headerRow = worksheet.addRow(headerRowValues);

    // 🔹 Header styling
    headerRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true, size: 12, name: "Calibri" };
      cell.alignment =
        colNumber <= 3
          ? { horizontal: "left", vertical: "middle" }
          : { horizontal: "center", vertical: "middle" };

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFEDEDED" }, // light grey
      };

      cell.border = {
        bottom: { style: "thin" },
      };
    });

    headerRow.height = 22;

    // 🔹 Agent rows
    agentYearlyData.forEach((agent) => {
      const rowData = [
        `${agent.firstname} ${agent.lastname}`,
        agent.role === "Admin" ? "Team Lead" : "Real Estate Agent",
        agent.email,
      ];

      metrics.forEach((metric) => {
        years.forEach((year) => {
          const stats = displayStats(agent.yearlyStats[year]);
          let value = stats[metric] || 0;

          if (metric === "avgSalePrice") value = value.toFixed(2);

          rowData.push(value);
        });
      });

      const row = worksheet.addRow(rowData);

      // 🔹 Row styling
      row.eachCell((cell, colNumber) => {
        cell.font = { size: 11, name: "Calibri" };
        cell.alignment =
          colNumber <= 3
            ? { horizontal: "left", vertical: "middle" }
            : { horizontal: "center", vertical: "middle" };

        if (typeof cell.value === "number" && colNumber > 3) {
          cell.numFmt = "#,##0"; // number formatting
        }
      });

      row.height = 18;
    });

    // 🔹 Freeze header row
    worksheet.views = [{ state: "frozen", ySplit: 1 }];

    // 🔹 Auto-width columns
    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const length = cell.value ? cell.value.toString().length : 10;
        if (length > maxLength) maxLength = length;
      });
      column.width = maxLength + 5;
    });

    // 🔹 Save file
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer]),
      `agent-production-${fromYear}-vs-${toYear}${
        includePending ? "-incl-pending" : ""
      }.xlsx`,
    );
  };

  useEffect(() => {
    if (!filteredFromYears.includes(fromYear)) {
      setFromYear(filteredFromYears[0]);
    }
  }, [toYear]);

  return (
    <div id="agent-production-tab" className="space-y-6">
      <div className="flex flex-wrap justify-between items-center  mb-6 bg-white p-4 lg:pl-8 rounded-xl shadow">
        <div className="flex flex-wrap items-center gap-4">
          <label className="font-medium text-gray-900">Compare:</label>
          <div className="relative">
            <button
              onClick={() => setFromVisible(!fromVisible)}
              className="border bg-white rounded-md px-3 py-1.5 text-left flex gap-x-3 items-center"
            >
              {fromYear}
              <ChevronDown size={16} />
            </button>
            <YearDropdown
              visible={fromVisible}
              options={filteredFromYears}
              selected={fromYear}
              onSelect={(year, clickedOutside) => {
                if (clickedOutside) return setFromVisible(false);
                setFromYear(year);
                setFromVisible(false);
              }}
            />
          </div>
          <p className="font-medium text-base">VS</p>

          {/* To Year */}
          <div className="relative">
            <button
              onClick={() => setToVisible(!toVisible)}
              className="border bg-white rounded-md px-3 py-1.5 flex items-center gap-x-3 text-left"
            >
              {toYear}
              <ChevronDown size={16} />
            </button>
            <YearDropdown
              visible={toVisible}
              options={toYears}
              selected={toYear}
              onSelect={(year, clickedOutside) => {
                if (clickedOutside) return setToVisible(false);
                setToYear(year);
                setToVisible(false);
              }}
            />
          </div>

          <IncludePendingToggle
            on={includePending}
            onToggle={() => setIncludePending((v) => !v)}
          />
        </div>

        <button
          onClick={() => exportToExcel(fromYear, toYear)}
          className="relative inline-flex items-center gap-2 px-5 sm:px-6 py-3 font-semibold text-white text-sm rounded-lg bg-gradient-to-r from-[#0f172a] to-[#1E293B] shadow-md hover:-translate-y-1 hover:shadow-lg transition duration-300 overflow-hidden group"
        >
          <span className="absolute inset-0 bg-white/20 rounded-full scale-0 group-hover:scale-150 transition-transform duration-700 ease-out"></span>
          <FileUp className="w-4 h-4" />
          <span className="relative">Export to Excel</span>
        </button>
      </div>

      {agentYearlyData.map((item, i) => (
        <div
          className={` ${
            item.type === 2 ? "bg-gray-200" : "bg-white"
          } rounded-xl overflow-hidden shadow mb-6`}
          key={i}
        >
          {/* Header */}
          <div
            className={`bg-gradient-to-r ${
              item.type === 2 ? "bg-gray-400" : "from-[#0f172a] to-[#1E293B]"
            }  text-white p-6 flex justify-between items-center`}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center font-bold text-lg">
                {getInitials(`${item.firstname} ${item.lastname}`)}
              </div>
              <div>
                <div className="text-xl font-bold">
                  {item.firstname} {item.lastname}
                </div>
                <div className="text-sm opacity-90">
                  {item.role === "Admin"
                    ? "Team Lead"
                    : item.role === "Employee"
                      ? "Real Estate Agent"
                      : "Client Care"}
                </div>
              </div>
            </div>
            {item.type === 2 && (
              <div
                className={`bg-gray-300 text-black rounded-full px-6 mt-2 py-1.5 w-fit text-sm`}
              >
                <p>Disabled</p>
              </div>
            )}
          </div>

          {/* Metrics Grid */}
          {/* Metrics Grid */}
          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-6   ${
              item.type === 2 ? "bg-gray-200" : "bg-gray-50"
            } p-6`}
          >
            {[
              "closedVolume",
              "dealsCount",
              "netCommission",
              "avgSalePrice",
            ].map((key) => {
              const fromStats = displayStats(item.yearlyStats[fromYear]);
              const toStats = displayStats(item.yearlyStats[toYear]);

              let percentChange = 0;
              let changeText = "";

              const fromVal = fromStats[key] || 0;
              const toVal = toStats[key] || 0;

              if (fromVal > 0) {
                percentChange = (toVal / fromVal) * 100 - 100;
                if (percentChange !== 0) {
                  const roundedPercent = parseFloat(
                    Math.abs(percentChange).toFixed(1),
                  ); // removes trailing .0
                  changeText =
                    percentChange > 0
                      ? `↑ ${roundedPercent}%`
                      : `↓ ${roundedPercent}%`;
                }
              } else if (toVal > 0) {
                // Agar fromVal 0 tha, new value aya
                changeText = `↑ 100%`;
              }

              return (
                <div
                  key={key}
                  className={` ${
                    item.type === 2 ? "bg-gray-100" : "bg-white"
                  } shadow rounded-md p-4 text-center`}
                >
                  <div className="text-sm font-semibold uppercase text-gray-500 mb-5">
                    {metricLabels[key]}
                  </div>
                  <div className="flex justify-center gap-x-10 mt-3">
                    {/* From Year */}
                    <div className="border-r border-gray-300 pr-10">
                      <p
                        className={`text-sm font-medium text-gray-400 ${
                          fromVal < toVal
                            ? "text-red-600"
                            : fromVal > toVal
                              ? "text-green-600"
                              : "text-gray-600"
                        }`}
                      >
                        {fromYear}
                      </p>
                      <div className="group">
                        <p className="text-xl font-semibold text-[#1E293B]">
                          {[
                            "avgSalePrice",
                            "closedVolume",
                            "netCommission",
                          ].includes(key)
                            ? `$${formatNumber(fromVal)}`
                            : fromVal}
                        </p>
                        <p className="hidden group-hover:block absolute bg-white p-1 rounded-lg border border-gray-200 shadow-sm text-xs">
                          {[
                            "avgSalePrice",
                            "closedVolume",
                            "netCommission",
                          ].includes(key)
                            ? formatCurrency(fromVal)
                            : fromVal}
                        </p>
                      </div>
                    </div>

                    {/* To Year */}
                    <div>
                      <p
                        className={`text-sm font-medium text-gray-400 ${
                          percentChange > 0
                            ? "text-green-600"
                            : percentChange < 0
                              ? "text-red-600"
                              : "text-gray-600"
                        }`}
                      >
                        {toYear}
                      </p>
                      <div className="group">
                        <p className="text-xl font-semibold text-[#1E293B]">
                          {[
                            "avgSalePrice",
                            "closedVolume",
                            "netCommission",
                          ].includes(key)
                            ? `$${formatNumber(toVal)}`
                            : toVal}
                        </p>

                        <p className="hidden group-hover:block absolute bg-white p-1 rounded-lg border border-gray-200 shadow-sm text-xs">
                          {[
                            "avgSalePrice",
                            "closedVolume",
                            "netCommission",
                          ].includes(key)
                            ? formatCurrency(toVal)
                            : toVal}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Appreciation / Depreciation */}
                  <p
                    className={`text-sm font-medium mt-3 ${
                      percentChange > 0
                        ? "text-green-600"
                        : percentChange < 0
                          ? "text-red-600"
                          : "text-gray-600"
                    }`}
                  >
                    {changeText}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
